/*
  Token.js
  -------
  Purpose: This page provides an interface for managing API tokens and authentication keys within the admin system. It allows administrators to view, create, manage, and revoke tokens for secure system access and integration.

  Main Logic:
  - Fetches and displays a list of API tokens with search and filtering capabilities.
  - Supports creation, revocation, and management of tokens and their permissions.
  - Integrates with API endpoints for token data operations.
  - Utilizes React hooks for state management and side effects.
  - Displays loading indicators and handles user feedback.
*/

import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import {
  Col,
  Container,
  Modal,
  Row,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import moment from "moment";

function Token() {
  const [searchInput, setSearchInput] = useState("");
  const [loader, setloader] = useState(false);
  const [tokenList, settokenList] = useState([]);
  const [selectedToken, setselectedToken] = useState([]);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const navigate = useNavigate();
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) {
      return "";
    }
    return moment(dateTimeString).format("D MMMM, YYYY | hh:mm A");
  };
  async function getTokenFn() {
    try {
      setloader(true);

      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/adminapi/social/auth_setting`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("admin_user_token"),
        },
      });

      setloader(false);

      if (!response.data.error) {
        settokenList(response.data?.data || []);
      } else {
        settokenList([]);
      }
    } catch (error) {
      if (error.response.status == 401) {
        localStorage.removeItem("mtm_user_token");
        localStorage.removeItem("mtm_user_data");
        navigate("/");
        return;
      }
      if (error.code === "ECONNABORTED") {
        console.log("Request timed out");
      } else {
        console.log("An error occurred:", error);
      }
      // setCurrentPage(0);
    } finally {
      setloader(false);
    }
  }
  const [submitting, setsubmitting] = useState(false);
  const [error, seterror] = useState("");
  async function postTokenFn() {
    try {
      setsubmitting(true);

      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/adminapi/social/auth_update`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("admin_user_token"),
        },
        data: {
          id: selectedToken?.id,
          refresh_token: selectedToken?.refresh_token,
        },
      });

      setsubmitting(false);

      if (!response.data.error) {
        handleClose();
        getTokenFn();
      } else {
        seterror(response.data.message);
      }
    } catch (error) {
      seterror(error?.response?.data?.message || error?.message);
      if (error.response.status == 401) {
        localStorage.removeItem("mtm_user_token");
        localStorage.removeItem("mtm_user_data");
        navigate("/");
        return;
      }
      if (error.code === "ECONNABORTED") {
        console.log("Request timed out");
      } else {
        console.log("An error occurred:", error);
      }
      // setCurrentPage(0);
    } finally {
      setsubmitting(false);
    }
  }
  const googleAuthUrl =
    "https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=454328450472-p9of5nnab6gopkd4dcme73s887a3op3d.apps.googleusercontent.com&redirect_uri=https://backend.dev-vison.infiniticube.in/googleapi/callback&scope=https://www.googleapis.com/auth/adwords https://www.googleapis.com/auth/webmasters&access_type=offline&prompt=consent";
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipRef = useRef(null);

  // Toggle tooltip visibility
  const handleClick = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsTooltipOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    getTokenFn();
  }, []);
  return (
    <>
      <Container fluid>
        <Row>
          {/* <Col sm={12}>
            <h2 className="fw-700 fs-30 pt-4 pb-2 flex items-center">
              Token{" "}
              <OverlayTrigger placement="top" overlay={renderTooltip}>
                <svg className="icon text-[26px] px-1 cursor-pointer">
                  <use href="#icon_info_circle"></use>
                </svg>
              </OverlayTrigger>
            </h2>
          </Col> */}
          <Col sm={12}>
            <h2 className="fw-700 fs-30 pt-4 pb-2 flex items-center ">
              Token{" "}
              <div className="relative">
                <svg
                  className="icon text-[24px] px-1 cursor-pointer"
                  onClick={handleClick}
                >
                  <use href="#icon_info_circle"></use>
                </svg>
                {/* Tooltip (Visible only when isTooltipOpen is true) */}
                {isTooltipOpen && (
                  <div
                    ref={tooltipRef}
                    className="absolute left-1/2 transform -translate-x-1/2 top-8 bg-gray-700 text-white text-xs px-3 py-2 rounded shadow-md w-max"
                  >
                    <a
                      href={googleAuthUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 underline"
                    >
                      URL to Generate Token
                    </a>
                  </div>
                )}
              </div>
            </h2>
          </Col>
        </Row>
        <Row>
          <Col sm={12} className="normal-tbl">
            <table>
              <tr className="tr-bg">
                <th>Client Id</th>
                <th>Refresh Token</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th></th>
              </tr>
              {loader && (
                <tr>
                  <td colSpan={6} className="text-center">
                    <Loader />
                  </td>
                </tr>
              )}
              <tr>
                {tokenList?.map((token) => (
                  <>
                    <td
                      style={{
                        width: "300px",
                        maxWidth: "300px",
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {token?.client_id}
                    </td>
                    <td
                      style={{
                        width: "300px",
                        maxWidth: "300px",
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {token?.refresh_token}
                    </td>
                    <td>{formatDateTime(token?.createdAt)}</td>
                    <td>{formatDateTime(token?.updatedAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="arrow-btn "
                        onClick={() => {
                          setselectedToken(token);
                          handleShow();
                        }}
                      >
                        <svg className="icon fs-24">
                          <use href="#icon_edit"></use>
                        </svg>
                      </button>
                    </td>
                  </>
                ))}
              </tr>
            </table>
          </Col>
        </Row>
      </Container>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Token</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            type="text"
            className="form-control"
            id="searchinput"
            placeholder="Token"
            value={selectedToken?.refresh_token}
            onChange={(e) => {
              setselectedToken({
                ...selectedToken,
                refresh_token: e.target.value,
              });
            }}
          />
          <div className="w-100 justify-content-end mt-4">
            <button
              type="submit"
              className="grd-btn rounded-pill mx-2 w-100"
              disabled={submitting}
              onClick={() => {
                postTokenFn();
              }}
            >
              {submitting ? <Loader /> : "Update"}
            </button>
            <div>{error && <p className="text-danger">{error}</p>}</div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default Token;
